import { FirebaseError } from 'firebase/app';
import {
  collection,
  collectionGroup,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  type DocumentData,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { getCropTypeById } from './cropType.service';
import { getRegionById } from './region.service';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomerCrop {
  id: string;           // compound: {cropTypeId}_{regionId}
  cropTypeId: string;
  regionId: string;
  isDeleted: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  deletedAt?: Timestamp;
}

export interface AddCustomerCropData {
  cropTypeId: string;
  regionId: string;
}

export interface CustomerCropServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cropsRef(orgId: string, customerId: string) {
  return collection(db, 'orgs', orgId, 'customers', customerId, 'crops');
}

function cropDocRef(orgId: string, customerId: string, cropId: string) {
  return doc(db, 'orgs', orgId, 'customers', customerId, 'crops', cropId);
}

function compoundId(cropTypeId: string, regionId: string): string {
  return `${cropTypeId}_${regionId}`;
}

function docToCustomerCrop(id: string, data: DocumentData): CustomerCrop {
  return {
    id,
    cropTypeId: data.cropTypeId as string,
    regionId: data.regionId as string,
    isDeleted: data.isDeleted as boolean,
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp,
    deletedAt: data.deletedAt as Timestamp | undefined,
  };
}

function toServiceError(error: unknown): CustomerCropServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

// ─── Service functions ────────────────────────────────────────────────────────

/** Returns all active (non-deleted) crop entries for a customer, ordered by creation time. */
export async function getCustomerCrops(orgId: string, customerId: string): Promise<CustomerCrop[]> {
  try {
    const q = query(
      cropsRef(orgId, customerId),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'asc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => docToCustomerCrop(d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Adds a crop entry for a customer.
 * - Document ID is `{cropTypeId}_{regionId}`, enforcing uniqueness per customer.
 * - Validates that both cropTypeId and regionId exist and are not soft-deleted.
 * - Throws if the combination is already active for this customer.
 * - Re-adding a previously soft-deleted combination resets its timestamps.
 * - Stores `orgId` on the crop doc to enable org-scoped collectionGroup queries.
 */
export async function addCustomerCrop(
  orgId: string,
  customerId: string,
  data: AddCustomerCropData,
): Promise<CustomerCrop> {
  try {
    const cropType = await getCropTypeById(orgId, data.cropTypeId);
    if (!cropType || cropType.isDeleted) {
      throw { code: 'not-found', message: 'The specified crop type does not exist or has been deleted.' };
    }

    const region = await getRegionById(orgId, data.regionId);
    if (!region || region.isDeleted) {
      throw { code: 'not-found', message: 'The specified region does not exist or has been deleted.' };
    }

    const id = compoundId(data.cropTypeId, data.regionId);
    const ref = cropDocRef(orgId, customerId, id);
    const existing = await getDoc(ref);
    if (existing.exists() && !(existing.data() as DocumentData).isDeleted) {
      throw {
        code: 'already-exists',
        message: 'This crop type + region combination is already assigned to this customer.',
      };
    }

    await setDoc(ref, {
      orgId,
      cropTypeId: data.cropTypeId,
      regionId: data.regionId,
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const snapshot = await getDoc(ref);
    return docToCustomerCrop(snapshot.id, snapshot.data()!);
  } catch (error) {
    if ((error as CustomerCropServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/** Soft-deletes a crop entry (sets isDeleted + deletedAt). */
export async function removeCustomerCrop(orgId: string, customerId: string, cropId: string): Promise<void> {
  try {
    await updateDoc(cropDocRef(orgId, customerId, cropId), {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Returns true if the customer has at least one active crop entry.
 * Use this before saving/updating a customer to enforce the at-least-one constraint.
 */
export async function hasAtLeastOneCrop(orgId: string, customerId: string): Promise<boolean> {
  try {
    const q = query(
      cropsRef(orgId, customerId),
      where('isDeleted', '==', false),
      limit(1),
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Returns the set of cropTypeIds actively assigned to at least one customer in the org.
 * Used by the Crop Types page to determine which entries cannot be deleted.
 * Crop docs store `orgId` to enable this org-scoped collectionGroup query.
 */
export async function getUsedCropTypeIds(orgId: string): Promise<Set<string>> {
  try {
    const q = query(
      collectionGroup(db, 'crops'),
      where('orgId', '==', orgId),
      where('isDeleted', '==', false),
    );
    const snapshot = await getDocs(q);
    const ids = new Set<string>();
    snapshot.docs.forEach(d => {
      const cropTypeId = (d.data() as DocumentData).cropTypeId as string;
      if (cropTypeId) ids.add(cropTypeId);
    });
    return ids;
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Returns the set of regionIds actively assigned to at least one customer in the org.
 * Used by the Regions page to determine which entries cannot be deleted.
 * Crop docs store `orgId` to enable this org-scoped collectionGroup query.
 */
export async function getUsedRegionIds(orgId: string): Promise<Set<string>> {
  try {
    const q = query(
      collectionGroup(db, 'crops'),
      where('orgId', '==', orgId),
      where('isDeleted', '==', false),
    );
    const snapshot = await getDocs(q);
    const ids = new Set<string>();
    snapshot.docs.forEach(d => {
      const regionId = (d.data() as DocumentData).regionId as string;
      if (regionId) ids.add(regionId);
    });
    return ids;
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Subscribes to real-time updates for all active crop entries of a customer.
 * Returns an unsubscribe function to clean up the listener.
 */
export function subscribeToCustomerCrops(
  orgId: string,
  customerId: string,
  onData: (_crops: CustomerCrop[]) => void,
  onError: (_error: CustomerCropServiceError) => void,
): Unsubscribe {
  const q = query(
    cropsRef(orgId, customerId),
    where('isDeleted', '==', false),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    snapshot => onData(snapshot.docs.map(d => docToCustomerCrop(d.id, d.data()))),
    err => onError(toServiceError(err)),
  );
}
